import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/client';
import DataTable from '@/components/common/DataTable';
import Modal from '@/components/common/Modal';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface Product {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  images: string[];
  supplierUrl: string | null;
  stock: number;
  isActive: boolean;
  hotProduct: boolean;
  dailyDeal: boolean;
  rate: number;
  brand: string | null;
  category: { id: string; name: string; slug: string };
  supplier: { id: string; name: string; code: string };
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface Supplier {
  id: string;
  name: string;
  code: string;
}

const BRAND_OPTIONS = [
  { key: 'CHANEL', label: 'Chanel' },
  { key: 'LOUIS_VUITTON', label: 'Louis Vuitton' },
  { key: 'GUCCI', label: 'Gucci' },
  { key: 'PRADA', label: 'Prada' },
  { key: 'DIOR', label: 'Dior' },
  { key: 'HERMES', label: 'Hermes' },
  { key: 'BALENCIAGA', label: 'Balenciaga' },
  { key: 'MIU_MIU', label: 'Miu Miu' },
  { key: 'BURBERRY', label: 'Burberry' },
  { key: 'YSL', label: 'YSL' },
  { key: 'NIKE', label: 'Nike' },
  { key: 'ADIDAS', label: 'Adidas' },
  { key: 'NEW_BALANCE', label: 'New Balance' },
  { key: 'POLO_RALPH_LAUREN', label: 'Polo Ralph Lauren' },
  { key: 'ASICS', label: 'Asics' },
  { key: 'MONCLER', label: 'Moncler' },
  { key: 'BIRKENSTOCK', label: 'Birkenstock' },
  { key: 'UGG', label: 'UGG' },
  { key: 'THE_NORTH_FACE', label: 'The North Face' },
  { key: 'LOEWE', label: 'Loewe' },
  { key: 'FENDI', label: 'Fendi' },
  { key: 'CARTIER', label: 'Cartier' },
  { key: 'ROLEX', label: 'Rolex' },
  { key: 'TORY_BURCH', label: 'Tory Burch' },
  { key: 'COACH', label: 'Coach' },
  { key: 'ALEXANDER_MCQUEEN', label: 'Alexander McQueen' },
  { key: 'BOTTEGA_VENETA', label: 'Bottega Veneta' },
  { key: 'VALENTINO', label: 'Valentino' },
  { key: 'DOLCE_GABBANA', label: 'Dolce & Gabbana' },
  { key: 'CALVIN_KLEIN', label: 'Calvin Klein' },
  { key: 'TOM_FORD', label: 'Tom Ford' },
  { key: 'CHRISTIAN_LOUBOUTIN', label: 'Christian Louboutin' },
  { key: 'ALO_YOGA', label: 'Alo Yoga' },
  { key: 'LULULEMON', label: 'Lululemon' },
  { key: 'GANNI', label: 'Ganni' },
  { key: 'AMIRI', label: 'Amiri' },
  { key: 'CELINE', label: 'Celine' },
  { key: 'CROCS', label: 'Crocs' },
  { key: 'PUMA', label: 'Puma' },
  { key: 'VANS', label: 'Vans' },
  { key: 'CONVERSE', label: 'Converse' },
  { key: 'HOKA', label: 'Hoka' },
  { key: 'ON_RUNNING', label: 'On Running' },
  { key: 'MARC_JACOBS', label: 'Marc Jacobs' },
  { key: 'LORO_PIANA', label: 'Loro Piana' },
  { key: 'ACNE_STUDIOS', label: 'Acne Studios' },
  { key: 'ISABEL_MARANT', label: 'Isabel Marant' },
  { key: 'JACQUEMUS', label: 'Jacquemus' },
  { key: 'OMEGA', label: 'Omega' },
  { key: 'BULGARI', label: 'Bulgari' },
  { key: 'VERSACE', label: 'Versace' },
  { key: 'GIVENCHY', label: 'Givenchy' },
  { key: 'JIMMY_CHOO', label: 'Jimmy Choo' },
  { key: 'MICHAEL_KORS', label: 'Michael Kors' },
  { key: 'TOMMY_HILFIGER', label: 'Tommy Hilfiger' },
  { key: 'LACOSTE', label: 'Lacoste' },
  { key: 'ZARA', label: 'Zara' },
  { key: 'BAPE', label: 'Bape' },
  { key: 'OFF_WHITE', label: 'Off-White' },
  { key: 'STONE_ISLAND', label: 'Stone Island' },
  { key: 'CANADA_GOOSE', label: 'Canada Goose' },
  { key: 'PATAGONIA', label: 'Patagonia' },
  { key: 'ARC_TERYX', label: "Arc'teryx" },
  { key: 'SALOMON', label: 'Salomon' },
  { key: 'TIMBERLAND', label: 'Timberland' },
  { key: 'DR_MARTENS', label: 'Dr. Martens' },
  { key: 'SAUCONY', label: 'Saucony' },
  { key: 'REEBOK', label: 'Reebok' },
  { key: 'JORDAN', label: 'Jordan' },
  { key: 'YEEZY', label: 'Yeezy' },
  { key: 'CHROME_HEARTS', label: 'Chrome Hearts' },
  { key: 'GOYARD', label: 'Goyard' },
  { key: 'CHLOE', label: 'Chloe' },
  { key: 'LONGCHAMP', label: 'Longchamp' },
  { key: 'MCM', label: 'MCM' },
  { key: 'VIVIENNE_WESTWOOD', label: 'Vivienne Westwood' },
  { key: 'KENZO', label: 'Kenzo' },
  { key: 'MOSCHINO', label: 'Moschino' },
  { key: 'DIESEL', label: 'Diesel' },
  { key: 'HUGO_BOSS', label: 'Hugo Boss' },
  { key: 'UNDER_ARMOUR', label: 'Under Armour' },
  { key: 'COLUMBIA', label: 'Columbia' },
  { key: 'MAMMUT', label: 'Mammut' },
  { key: 'OTHER', label: 'Other' },
];

const brandKeyToLabel = Object.fromEntries(BRAND_OPTIONS.map(b => [b.key, b.label]));

export default function ProductsPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleteSelectedDialogOpen, setIsDeleteSelectedDialogOpen] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: 0,
    images: [''],
    supplierUrl: '',
    stock: 0,
    isActive: true,
    hotProduct: false,
    dailyDeal: false,
    rate: 100,
    brand: '',
    categoryId: '',
    supplierId: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [filterCategoryId, filterBrand, currentPage, searchQuery]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, suppliersRes] = await Promise.all([
        adminApi.getProducts({ page: 1, limit: PAGE_SIZE }),
        adminApi.getCategories(),
        adminApi.getSuppliers(),
      ]);
      setProducts(productsRes.data);
      setTotalPages(productsRes.meta?.totalPages || 1);
      setTotalProducts(productsRes.meta?.total || productsRes.data.length);
      setCategories(categoriesRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const params: Record<string, unknown> = { page: currentPage, limit: PAGE_SIZE };
      if (filterCategoryId) {
        params.categoryId = filterCategoryId;
      }
      if (filterBrand) {
        params.brand = filterBrand;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const productsRes = await adminApi.getProducts(params);
      setProducts(productsRes.data);
      setTotalPages(productsRes.meta?.totalPages || 1);
      setTotalProducts(productsRes.meta?.total || productsRes.data.length);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const openCreateModal = () => {
    setSelectedProduct(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      price: 0,
      images: [''],
      supplierUrl: '',
      stock: 0,
      isActive: true,
      hotProduct: false,
      dailyDeal: false,
      rate: 100,
      brand: '',
      categoryId: categories[0]?.id || '',
      supplierId: suppliers[0]?.id || '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      price: product.price,
      images: product.images.length > 0 ? product.images : [''],
      supplierUrl: product.supplierUrl || '',
      stock: product.stock,
      isActive: product.isActive,
      hotProduct: product.hotProduct || false,
      dailyDeal: product.dailyDeal || false,
      rate: product.rate || 100,
      brand: product.brand || '',
      categoryId: product.category.id,
      supplierId: product.supplier.id,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const data = {
        ...formData,
        images: formData.images.filter(img => img.trim() !== ''),
        brand: formData.brand || null,
      };

      if (selectedProduct) {
        await adminApi.updateProduct(selectedProduct.id, data);
      } else {
        await adminApi.createProduct(data);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;
    setIsSaving(true);

    try {
      await adminApi.deleteProduct(selectedProduct.id);
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);

    try {
      await adminApi.deleteAllProducts();
      setIsDeleteAllDialogOpen(false);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      console.error('Error deleting all products:', error);
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleDeleteSelected = async () => {
    setIsDeletingSelected(true);

    try {
      await adminApi.deleteSelectedProducts(Array.from(selectedIds));
      setIsDeleteSelectedDialogOpen(false);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      console.error('Error deleting selected products:', error);
    } finally {
      setIsDeletingSelected(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  const toggleSelectProduct = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = products.length > 0 && selectedIds.size === products.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < products.length;

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={isAllSelected}
          ref={(el) => {
            if (el) el.indeterminate = isSomeSelected;
          }}
          onChange={toggleSelectAll}
          className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
        />
      ) as unknown as string,
      render: (product: Product) => (
        <input
          type="checkbox"
          checked={selectedIds.has(product.id)}
          onChange={() => toggleSelectProduct(product.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
        />
      ),
    },
    {
      key: 'name',
      label: t('products.name'),
      render: (product: Product) => (
        <div className="flex items-center gap-3">
          {product.images[0] && (
            <img src={product.images[0]} alt={product.name} className="w-10 h-10 rounded object-cover" />
          )}
          <span className="font-medium">{product.name}</span>
        </div>
      ),
    },
    {
      key: 'price',
      label: t('products.price'),
      render: (product: Product) => `$${product.price.toFixed(2)}`,
    },
    {
      key: 'stock',
      label: t('products.stock'),
    },
    {
      key: 'category',
      label: t('products.category'),
      render: (product: Product) => product.category.name,
    },
    {
      key: 'brand',
      label: t('products.brand'),
      render: (product: Product) => product.brand ? (brandKeyToLabel[product.brand] || product.brand) : '-',
    },
    {
      key: 'status',
      label: t('products.status'),
      render: (product: Product) => (
        <span className={`px-2 py-1 text-xs rounded-full ${product.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
          {product.isActive ? t('products.active') : t('products.inactive')}
        </span>
      ),
    },
    {
      key: 'actions',
      label: t('common.actions'),
      render: (product: Product) => (
        <div className="flex gap-2">
          <button onClick={() => openEditModal(product)} className="p-1 text-gray-500 hover:text-primary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => { setSelectedProduct(product); setIsDeleteDialogOpen(true); }}
            className="p-1 text-gray-500 hover:text-red-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('products.title')}</h1>
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t('common.search')}
              className="w-48 sm:w-64 pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <select
            value={filterCategoryId}
            onChange={(e) => {
              setFilterCategoryId(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">{t('products.allCategories')}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <select
            value={filterBrand}
            onChange={(e) => {
              setFilterBrand(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">{t('products.allBrands')}</option>
            {BRAND_OPTIONS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
          <button onClick={openCreateModal} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm sm:text-base whitespace-nowrap">
            {t('products.addProduct')}
          </button>
          {selectedIds.size > 0 && (
            <button
              onClick={() => setIsDeleteSelectedDialogOpen(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base whitespace-nowrap"
            >
              {t('products.deleteSelected')} ({selectedIds.size})
            </button>
          )}
          {products.length > 0 && selectedIds.size === 0 && (
            <button
              onClick={() => setIsDeleteAllDialogOpen(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base whitespace-nowrap"
            >
              {t('products.deleteAll')}
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={products}
        isLoading={isLoading}
        keyExtractor={(product) => product.id}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              {t('common.showing')} {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, totalProducts)} {t('common.of')} {totalProducts} {t('products.title').toLowerCase()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous')}
            </button>
            <span className="px-3 py-1 text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedProduct ? t('products.editProduct') : t('products.addProduct')} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.slug')}</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.price')}</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.stock')}</label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.category')}</label>
              <select
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.supplier')}</label>
              <select
                value={formData.supplierId}
                onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                {suppliers.map((sup) => (
                  <option key={sup.id} value={sup.id}>{sup.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.brand')}</label>
            <select
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">-- No Brand --</option>
              {BRAND_OPTIONS.map((b) => (
                <option key={b.key} value={b.key}>{b.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.supplierUrl')}</label>
            <input
              type="url"
              value={formData.supplierUrl}
              onChange={(e) => setFormData({ ...formData, supplierUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="https://s.flylinking.com/g-XXXXXXXX"
            />
            <p className="text-xs text-gray-500 mt-1">{t('products.supplierUrlHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('products.images')}</label>
            {formData.images.map((img, index) => (
              <div key={index} className="flex gap-2 mb-2 items-center">
                {img && (
                  <img
                    src={img}
                    alt={`Preview ${index + 1}`}
                    className="w-12 h-12 object-cover rounded border border-gray-200 flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <input
                  type="url"
                  value={img}
                  onChange={(e) => {
                    const newImages = [...formData.images];
                    newImages[index] = e.target.value;
                    setFormData({ ...formData, images: newImages });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Image URL"
                />
                {formData.images.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) })}
                    className="p-2 text-red-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, images: [...formData.images, ''] })}
              className="text-sm text-primary hover:underline"
            >
              + Add image
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-primary"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700">{t('products.active')}</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hotProduct"
                checked={formData.hotProduct}
                onChange={(e) => setFormData({ ...formData, hotProduct: e.target.checked })}
                className="w-4 h-4 text-orange-500"
              />
              <label htmlFor="hotProduct" className="text-sm font-medium text-gray-700">🔥 Hot Product</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="dailyDeal"
                checked={formData.dailyDeal}
                onChange={(e) => setFormData({ ...formData, dailyDeal: e.target.checked })}
                className="w-4 h-4 text-yellow-500"
              />
              <label htmlFor="dailyDeal" className="text-sm font-medium text-gray-700">⚡ Daily Deal</label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Rate (1-100)</label>
            <input
              type="number"
              value={formData.rate}
              onChange={(e) => setFormData({ ...formData, rate: Math.min(100, Math.max(1, Number(e.target.value))) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              min="1"
              max="100"
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              {isSaving ? 'Saving...' : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('products.deleteProduct')}
        message={t('products.deleteConfirm')}
        isLoading={isSaving}
      />

      <ConfirmDialog
        isOpen={isDeleteAllDialogOpen}
        onClose={() => setIsDeleteAllDialogOpen(false)}
        onConfirm={handleDeleteAll}
        title={t('products.deleteAllProducts')}
        message={t('products.deleteAllConfirm')}
        isLoading={isDeletingAll}
      />

      <ConfirmDialog
        isOpen={isDeleteSelectedDialogOpen}
        onClose={() => setIsDeleteSelectedDialogOpen(false)}
        onConfirm={handleDeleteSelected}
        title={t('products.deleteSelectedProducts')}
        message={t('products.deleteSelectedConfirm', { count: selectedIds.size })}
        isLoading={isDeletingSelected}
      />
    </div>
  );
}
